import { useAuthStore } from "@/store/useAuthStore";
import { getAdminTrackingPath } from "@/utils/authRedirect";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TopNav } from "./TopNav";

const pageTitles: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/live": "Live Monitor",
  "/employees": "Employee Activity",
  "/screenshots": "Screenshots",
  "/attendance": "Attendance",
  "/reports": "Reports",
  "/settings": "Settings",
};

export function PrivateLayout() {
  const {
    isAuthenticated,
    hasOrg,
    isPlatformAdmin,
    isCrmMember,
    organizations,
  } = useAuthStore();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebar-collapsed");
    return stored === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // PLATFORM_ADMIN → go to admin dashboard
  if (isPlatformAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  if (isCrmMember()) {
    if (!hasOrg()) {
      if (organizations.length > 0) {
        return <Navigate to="/select-org" state={{ from: location }} replace />;
      }

      return <Navigate to="/onboarding" state={{ from: location }} replace />;
    }

    const adminPath =
      getAdminTrackingPath(location.pathname) || "/admin/dashboard";

    return (
      <Navigate to={`${adminPath}${location.search}${location.hash}`} replace />
    );
  }

  // Subscriber without a selected org
  if (!isPlatformAdmin() && !hasOrg()) {
    // Has orgs — just hasn't picked one this session
    if (organizations.length > 0) {
      return <Navigate to="/select-org" state={{ from: location }} replace />;
    }
    // No orgs at all → create first one
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  const baseRoute = "/" + location.pathname.split("/").filter(Boolean)[0];
  const title =
    pageTitles[baseRoute] ||
    (location.pathname.startsWith("/employees/")
      ? "Employee Detail"
      : "Tracker");

  return (
    <div className="flex h-screen overflow-hidden bg-surface-base">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopNav title={title} />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
